/**
 * What the Assistant looked up, as a person would say it.
 *
 * The replies used to narrate this - "Let me search for all of them at once.
 * Good, I have AC1-4 candidates." - which is the model talking to itself. The
 * server now keeps only the last round's words as the reply, and this turns the
 * turn's lookups into one quiet, openable line instead.
 *
 * Only lookups count. Proposals, navigation and questions are what the turn
 * did, and each already has its own card.
 */

import type { AssistantToolCall } from './assistant-history.ts';

function quoted(values: unknown): string {
  const list = (Array.isArray(values) ? values : [values])
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .map((v) => `“${v.trim()}”`);
  return list.join(', ');
}

function describe(call: AssistantToolCall): string | null {
  const input = (call.input ?? {}) as Record<string, unknown>;
  const ship = typeof input.ship_id === 'string' ? ` on ${input.ship_id}` : '';
  switch (call.name) {
    case 'search_columns':
      return `Signals matching ${quoted(input.queries) || 'the request'}${ship}`;
    case 'search_ships':
      return input.query ? `Ships matching ${quoted(input.query)}` : 'The ships with data';
    case 'get_columns':
      return `Every signal${ship}`;
    case 'get_column_values':
      return `Values of ${String(input.column ?? 'a signal')}${ship}`;
    case 'get_stats': {
      const n = Array.isArray(input.columns) ? input.columns.length : 0;
      return `Statistics for ${n} signal${n === 1 ? '' : 's'}${ship}`;
    }
    case 'find_legs':
      return `Recent voyage legs${ship}`;
    case 'search_catalog':
      return input.query ? `Saved charts matching ${quoted(input.query)}` : 'Saved charts';
    case 'argus_find_series':
      return input.query ? `Series matching ${quoted(input.query)}` : 'Series in the rule’s scope';
    case 'argus_list_rules':
      return 'Argus rules';
    case 'argus_get_rule':
      return 'An Argus rule';
    case 'argus_check_rule':
      return 'Checked a draft rule';
    default:
      return null;
  }
}

export function lookupLines(calls: ReadonlyArray<AssistantToolCall>): string[] {
  return calls.map(describe).filter((line): line is string => line !== null);
}
